/**
 * Phase 8 T-25 §29.B — renderBoldKeywords
 *
 * Parses a `paraphrase` string containing `**keyword**` markdown bolds and
 * returns a sequence of React nodes where each bolded span is rendered as
 * `<mark class="ggj-keyword">…</mark>`.
 *
 * This is the ONLY approved use of `<mark>` in the app — grep check in
 * §29.B keeps us honest (`rg '<mark' src/` must return hits only from
 * this file and its direct consumers).
 *
 * Rules:
 *   - `**word**` → `<mark class="ggj-keyword">word</mark>`
 *   - nested bolds are not supported (falls through as literal).
 *   - line breaks are preserved as `<br />` to keep the paraphrase's
 *     paragraph shape without requiring a full markdown renderer.
 */

import { Fragment, type ReactNode } from 'react';

const BOLD_RE = /\*\*([^*\n]+)\*\*/g;

/**
 * Render a markdown-lite string with `**keyword**` spans highlighted.
 *
 * @param source - The raw paraphrase string from the agent.
 * @returns An array of React nodes safe to drop into a `<p>` or a fragment.
 */
export function renderBoldKeywords(source: string): ReactNode[] {
  if (!source) return [];
  const out: ReactNode[] = [];
  let lastIndex = 0;
  let keyCounter = 0;
  BOLD_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = BOLD_RE.exec(source)) !== null) {
    if (match.index > lastIndex) {
      out.push(
        renderPlain(source.slice(lastIndex, match.index), `t-${keyCounter++}`),
      );
    }
    out.push(
      <mark key={`k-${keyCounter++}`} className="ggj-keyword">
        {match[1]}
      </mark>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < source.length) {
    out.push(renderPlain(source.slice(lastIndex), `t-${keyCounter++}`));
  }
  return out;
}

function renderPlain(text: string, key: string): ReactNode {
  if (!text.includes('\n')) {
    return <Fragment key={key}>{text}</Fragment>;
  }
  const lines = text.split('\n');
  return (
    <Fragment key={key}>
      {lines.map((line, i) => (
        <Fragment key={i}>
          {i > 0 ? <br /> : null}
          {line}
        </Fragment>
      ))}
    </Fragment>
  );
}
